import { useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { OrganizationType } from '../../../constants/orgType';
import { Prefecture } from '../../../constants/prefecture';
import { Organization } from '../../../models';
import { Gender } from '../../../constants/gender';
import { PatientCareLevel } from '../../../constants/careLevel';
import { IndependentLifeLevel } from '../../../constants/independentLifeLevel';
import { DentalIntervention } from '../../../constants/dentalIntervention';
import { getBaseConstantsOptions, getEnumOptions } from '../../../utils/option-utils';

export type PatientSearchFormValues = {
  organizationId?: string;
  organizationPrefecture?: string;
  organizationType?: string;
  name?: string;
  gender?: string;
  careLevel?: string;
  independentLifeLevel?: string;
  dentalIntervention?: string;
};

export type Props = {
  organizations: Organization[];
  onSearch: (values: PatientSearchFormValues) => void;
  onReset: () => void;
};

export const PatientSearchForm = (props: Props) => {
  const { organizations, onSearch, onReset } = props;
  const [form] = Form.useForm();

  // オプションをメモ化
  const orgOptions = useMemo(
    () =>
      organizations
        ?.filter((org) => (org.accounts?.length ?? 0) > 0)
        .map((org) => ({
          label: org.name || '名称未設定',
          value: org.id,
        })) || [],
    [organizations],
  );

  const prefectureOptions = useMemo(() => getEnumOptions(Prefecture), []);

  const organizationTypeOptions = useMemo(
    () => getBaseConstantsOptions(OrganizationType.getAll()),
    [],
  );

  const genderOptions = useMemo(
    () => getBaseConstantsOptions(Gender.getAll()),
    [],
  );

  const careLevelOptions = useMemo(
    () => getBaseConstantsOptions(PatientCareLevel.getAll()),
    [],
  );

  const lifeLevelOptions = useMemo(
    () => getBaseConstantsOptions(IndependentLifeLevel.getAll()),
    [],
  );

  const dentalInterventionOptions = useMemo(
    () => getBaseConstantsOptions(DentalIntervention.getAll()),
    [],
  );

  const handleSearch = () => {
    const values = form.getFieldsValue();
    // undefined値を除外してパラメータをクリーンにする
    const cleanParams = Object.entries(values).reduce(
      (acc: any, [key, value]) => {
        if (value !== undefined && value !== '' && value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
    onSearch(cleanParams);
  };

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  return (
    <Card title="検索条件" styles={{ body: { paddingBottom: 8 } }}>
      <Form form={form} layout="vertical" style={{ marginBottom: 0 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="organizationId"
              label="施設名"
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="施設を選択"
                allowClear
                showSearch
                optionFilterProp="label"
                options={orgOptions}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="organizationPrefecture"
              label="都道府県"
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="都道府県を選択"
                allowClear
                showSearch
                optionFilterProp="label"
                options={prefectureOptions}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="organizationType"
              label="施設種別"
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="施設種別を選択"
                allowClear
                showSearch
                optionFilterProp="label"
                options={organizationTypeOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={6}>
            <Form.Item name="name" label="患者名" style={{ marginBottom: 12 }}>
              <Input placeholder="患者名で検索" allowClear />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="gender" label="性別" style={{ marginBottom: 12 }}>
              <Select
                placeholder="性別"
                allowClear
                options={genderOptions}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name="careLevel"
              label="要介護度"
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="要介護度"
                allowClear
                options={careLevelOptions}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              name="independentLifeLevel"
              label="自立度"
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="自立度"
                allowClear
                options={lifeLevelOptions}
              />
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item
              name="dentalIntervention"
              label="歯科介入"
              style={{ marginBottom: 12 }}
            >
              <Select
                placeholder="歯科介入"
                allowClear
                options={dentalInterventionOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item style={{ marginBottom: 12, textAlign: 'right' }}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  リセット
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  検索
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
